import { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import { CloudSync, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import oneDriveService from '../services/oneDriveService';

const ActiveJobsBadge = ({ onJobClick }) => {
  const [activeJobs, setActiveJobs] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    loadActiveJobs();
    
    const interval = setInterval(() => {
      loadActiveJobs();
    }, 10000); // Actualizar cada 10 segundos

    return () => clearInterval(interval);
  }, []);

  const loadActiveJobs = async () => {
    try {
      const response = await oneDriveService.getActiveJobs();
      setActiveJobs(response.data || []);
    } catch (error) {
      console.error('Error loading active jobs:', error);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleJobClick = (job) => {
    handleClose();
    if (onJobClick) {
      onJobClick(job);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'primary';
      case 'paused': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'processing': return 'Procesando';
      case 'paused': return 'Pausado';
      case 'pending': return 'Pendiente';
      default: return status;
    }
  };

  const open = Boolean(anchorEl);

  if (activeJobs.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton onClick={handleClick} color="primary">
        <Badge badgeContent={activeJobs.length} color="error">
          <CloudSync />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ width: 350, maxHeight: 400 }}>
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Importaciones Activas
            </Typography>
            <Typography variant="body2">
              {activeJobs.length} trabajo{activeJobs.length !== 1 ? 's' : ''} en progreso
            </Typography>
          </Box>

          <List sx={{ p: 0 }}>
            {activeJobs.map((job, index) => (
              <Box key={job.id}>
                {index > 0 && <Divider />}
                <ListItemButton onClick={() => handleJobClick(job)}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {job.jobType === 'new_proceedings' ? 'Nuevos Expedientes' : 'Expedientes Existentes'}
                        </Typography>
                        <Chip
                          label={getStatusText(job.status)}
                          size="small"
                          color={getStatusColor(job.status)}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                          {job.processedFiles} / {job.totalFiles} archivos
                        </Typography>
                        <Typography variant="caption" color="success.main">
                          ✓ {job.successCount} exitosos
                        </Typography>
                        {job.failedCount > 0 && (
                          <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
                            ✗ {job.failedCount} fallidos
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export default ActiveJobsBadge;
